import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormattedMessage, useIntl } from "react-intl";
import ControlledSelect from "../../../../components/ControlledInputs/select";
import {
  ModalContent,
  ModalTitle,
  FormContainer,
  LabelContainer,
  LabelText,
  ButtonsContainer,
  CancelButton,
  ContinueButton,
  SelectDropdownStyles,
  EventModalContainer,
  EventModalStyles
} from "./styles";

const EventSelectionModal = ({
  isOpen,
  onClose,
  onEventSelect,
  events = [],
  years = [],
  onYearSelect,
  loading = false
}) => {
  const intl = useIntl();
  const eventSchema = z.object({
    selectedYear: z.object({
      value: z.number(),
      label: z.string(),
    }, {
      required_error: intl.formatMessage({ id: "report_filter_year_required" }),
    }),
    selectedEvent: z.object({
      value: z.number(),
      label: z.string(),
    }, {
      required_error: intl.formatMessage({ id: "required_field" }),
    }),
  });

  const form = useForm({
    resolver: zodResolver(eventSchema),
    mode: "onChange",
    defaultValues: {
      selectedYear: null,
      selectedEvent: null,
    },
  });

  const { control, formState, handleSubmit, reset, setValue, watch } = form;
  const selectedYear = watch("selectedYear");

  const handleYearChange = (option) => {
    setValue("selectedEvent", null, { shouldValidate: true });
    if (onYearSelect) {
      onYearSelect(option?.value ?? null);
    }
  };

  const onSubmit = (data) => {
    if (data.selectedEvent) {
      onEventSelect(data.selectedEvent, data.selectedYear?.value ?? null);
      reset();
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <>
      <SelectDropdownStyles />
      <EventModalContainer
        isOpen={isOpen}
        onRequestClose={handleCancel}
        style={EventModalStyles}
        id="event-selection-modal"
      >
        <ModalContent>
          <ModalTitle>
            <FormattedMessage id="event_selection.modal_title" />
          </ModalTitle>
          
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            <LabelContainer>
              <LabelText>
                {intl.formatMessage({ id: "year" })}
                <span className="required-asterisk">*</span>
              </LabelText>
              <ControlledSelect
                data-test="year-selection-select"
                name="selectedYear"
                control={control}
                formState={formState}
                label=""
                placeholder={intl.formatMessage({ id: "year" })}
                options={years}
                onChange={handleYearChange}
              />
            </LabelContainer>

            <LabelContainer>
              <LabelText>
                Evento<span className="required-asterisk">*</span>
              </LabelText>
              <ControlledSelect
                data-test="event-selection-select"
                name="selectedEvent"
                control={control}
                formState={formState}
                label=""
                placeholder={intl.formatMessage({ id: "event_selection.event_placeholder" })}
                options={events}
                disabled={!selectedYear}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    width: "100%",
                    height: "40px",
                    borderRadius: "8px",
                    borderColor: formState.errors.selectedEvent ? "#ef4444" : "#d1d5db",
                    boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.5)" : "none",
                    transition: "all 300ms",
                    "&:hover": {
                      borderColor: formState.errors.selectedEvent ? "#ef4444" : "#9ca3af",
                    },
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    minHeight: "40px",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    fontFamily: "'Open Sans', sans-serif",
                    fontSize: "12px",
                    fontWeight: 400,
                    fontStyle: "regular",
                    lineHeight: "18.86px",
                    color: "#000000",
                  }),
                  singleValue: (base) => ({
                    ...base,
                    fontFamily: "'Open Sans', sans-serif",
                    fontSize: "12px",
                    fontWeight: 400,
                    fontStyle: "regular",
                    lineHeight: "18.86px",
                    color: "#000000",
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontFamily: "'Open Sans', sans-serif",
                    fontSize: "12px",
                    fontWeight: 400,
                    fontStyle: "regular",
                    lineHeight: "18.86px",
                    color: "#000000",
                    backgroundColor: state.isSelected ? "#e5e7eb" : state.isFocused ? "#f3f4f6" : "white",
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: "9999999999999",
                  }),
                }}
              />
            </LabelContainer>

            <ButtonsContainer>
              <CancelButton type="button" onClick={handleCancel}>
                <FormattedMessage id="event_selection.cancel_button" />
              </CancelButton>
              <ContinueButton 
                type="submit" 
                disabled={!formState.isValid || loading}
              >
                <FormattedMessage id="event_selection.continue_button" />
              </ContinueButton>
            </ButtonsContainer>
          </FormContainer>
        </ModalContent>
      </EventModalContainer>
    </>
  );
};

export default EventSelectionModal;